// src/services/api/dashboardService.ts

import {
  ProjectionsClientBalanceDetailResponse,
  DashboardDataRequest,
} from '@typeDefs/domain/dashboard';
import apiClient from './client';

/**
 * Dashboard service for fetching and managing dashboard-related data
 * Uses the generated API client methods instead of hardcoded endpoints
 */
export const dashboardService = {
  /**
   * Fetch client balance details from the projections endpoint
   * Uses the generated projectionsClientBalanceDetail method from api.ts
   */
  getClientBalanceDetail: async (
    corporateClientId: string,
    _options?: Partial<DashboardDataRequest>,
  ): Promise<ProjectionsClientBalanceDetailResponse> => {
    try {
      if (import.meta.env.MODE !== 'test') {
        console.log(`Fetching dashboard data for corporate client: ${corporateClientId}`);
      }

      // Use the generated API method instead of hardcoded endpoint
      const response = await apiClient.api.projectionsClientBalanceDetail(corporateClientId);

      if (import.meta.env.MODE !== 'test') {
        console.log('Dashboard API Response:', {
          status: response.status,
          hasData: !!response.data,
          dataKeys: response.data ? Object.keys(response.data) : [],
        });
      }

      return response.data;
    } catch (error: unknown) {
      console.error(
        'Failed to fetch dashboard data:',
        error instanceof Error ? error.message : error,
      );
      throw new Error(
        `Failed to fetch dashboard data: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  },

  /**
   * Get position snapshots (if needed for additional dashboard metrics)
   */
  getPositionSnapshots: async (corporateClientId: string) => {
    try {
      const response =
        await apiClient.api.projectionsPositionSnapshotsLatestDetail(corporateClientId);
      return response.data;
    } catch (error: unknown) {
      console.error(
        'Failed to fetch position snapshots:',
        error instanceof Error ? error.message : error,
      );
      throw new Error(
        `Failed to fetch position snapshots: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  },

  /**
   * Get client fees (if needed for dashboard calculations)
   */
  getClientFees: async (
    corporateClientId: string,
    pageNumber: number = 1,
    pageSize: number = 50,
  ) => {
    try {
      const response = await apiClient.api.projectionsClientFeesDetail(corporateClientId, {
        pageNumber: pageNumber,
        pageSize: pageSize,
      });
      return response.data;
    } catch (error: unknown) {
      console.error('Failed to fetch client fees:', error instanceof Error ? error.message : error);
      throw new Error(
        `Failed to fetch client fees: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  },
};
